import { useEffect, useState } from "react";
import { Check, CheckCircle2, ExternalLink, FileText, Loader2, Lock, PenLine } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { fmtMoney } from "../proposal/format";
import { isUkCountry } from "../../lib/ukCountry";
import { Field, GhostButton, Notice, PrimaryButton, Section, Stat, StepHeader, TextInput } from "./fields";
import { SharedDetailsFields } from "./SharedDetailsFields";
import { deadlineBlocks } from "./DeadlineNote";
import { SignaturePad } from "./SignaturePad";
import { SowReviewModal } from "./SowReviewModal";
import { AmendmentNotice } from "./AmendmentNotice";
import { VersionHistory } from "./VersionHistory";
import { ChangeRequestBox, SpecialConditionsNotice } from "./ChangeRequestBox";

function SaveIndicator({ saving }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-wide text-stone-500" data-testid="commission-save-indicator">
      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-emerald-600" />}
      {saving ? "Saving" : "Saved as you type"}
    </span>
  );
}

// Route A: complete the commissioning details, review the draft
// Statement of Work, accept the Terms and sign online.
export function MedicysSowStep({ state, answers, set, onSign, onBack, busy, error, previewUrl, saving, invalid, checking, onDeadlineCheck,
  amendment = null, returned = null, versions = [], versionUrl = null, onRequestChange = null }) {
  const [signaturePng, setSignaturePng] = useState(null);
  const [terms, setTerms] = useState(false);
  const [typedName, setTypedName] = useState(answers.signatory_name || "");
  const [openedHere, setOpenedHere] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const draftOpened = openedHere || !!state.draft_opened_at;
  const openReview = () => { setOpenedHere(true); setReviewOpen(true); };
  const agreeInReview = () => {
    setOpenedHere(true); setTerms(true); setReviewOpen(false);
    setTimeout(() => document.querySelector("[data-testid='commission-signing']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  const totals = state.totals || {};
  const vatYes = isUkCountry(answers.invoice_address?.country);
  const vatAmount = vatYes ? Math.round(totals.net_total * 20) / 100 : 0;
  const total = Math.round((totals.net_total + vatAmount) * 100) / 100;
  // The server refused the signature because a detail is missing: unlock the details so the client can fix it.
  useEffect(() => {
    if (invalid && invalid.size) setTerms(false);
  }, [invalid]);
  const nameToSign = typedName || answers.signatory_name || "";
  const nameComplete = nameToSign.trim().split(/\s+/).filter(Boolean).length >= 2;
  const ready = draftOpened && terms && !!signaturePng && nameComplete;
  const company = state.client_company || "your organisation";

  return (
    <div className="flex flex-col gap-8" data-testid="commission-medicys-sow">
      <StepHeader
        eyebrow={amendment ? `Step 3 of 5 · Version ${amendment.version}` : "Step 3 of 5 · Project details"}
        title={amendment ? "Check your details and sign the amended Statement of Work" : returned ? "Make the change and sign again" : "Let us complete the Statement of Work"}
        intro={amendment || returned ? null : "A few details so that the document, the invoices and the project updates reach the right people. Your answers are saved as you type, so you can come back later if you need to."}
        aside={<div className="flex items-center gap-4"><SaveIndicator saving={saving} />{onBack && <GhostButton onClick={onBack} disabled={busy} testid="commission-back-to-routes">Change route</GhostButton>}</div>}
      />
      <AmendmentNotice amendment={amendment} returned={returned} />
      {amendment && versionUrl && <VersionHistory versions={versions} versionUrl={versionUrl} />}

      {state.stage === "sign_failed" && (
        <Notice tone="danger" title="We could not complete the signing last time" testid="commission-sign-failed">
          Nothing has been lost. Please check your details below and sign again. The Medicys team has been told about the problem and will be in touch if it happens again.
        </Notice>
      )}

      <SharedDetailsFields a={answers} set={set} forSigning startAt={1} onFile={state.client_record} invalid={invalid} deadline={state.deadline} timeline={state.timeline} onDeadlineCheck={onDeadlineCheck} checking={checking} locked={terms} onUnlock={() => setTerms(false)} />

      {!deadlineBlocks(answers, state.deadline) && (
        <>
      <Section n="05" title="Your Statement of Work" tone="dark" intro="The document is built from this proposal and the details above. Please read it in full before signing." testid="commission-review">
        <div className="flex flex-col gap-4 rounded-lg border border-white/10 bg-white/[0.04] p-5 sm:p-6" data-testid="sow-review-box">
          <p className="text-sm text-stone-300">You will be asked to confirm that you have read this before you sign.</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              type="button"
              onClick={openReview}
              data-testid="sow-preview-link"
              data-state={draftOpened ? "opened" : "unopened"}
              className={`inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full px-6 py-3.5 text-base font-semibold whitespace-nowrap transition-[background-color,color,transform] active:scale-[0.98] ${
                draftOpened ? "border-2 border-emerald-400/70 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/20" : "bg-[#3EA8DC] text-[#0B1220] hover:bg-[#5FBCE6]"
              }`}
            >
              {draftOpened ? <CheckCircle2 className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
              {draftOpened ? "Draft opened" : amendment ? `Review version ${amendment.version} of the Statement of Work` : "Review the draft Statement of Work"}
            </button>
            <p className="text-xs text-stone-400 leading-relaxed" data-testid="sow-preview-note">
              {draftOpened
                ? "You can open it again at any time. The draft reflects the answers saved so far and is marked as a draft until it is signed."
                : "Opens here on the page. The draft reflects the answers saved so far and is marked as a draft until it is signed."}
              {" "}
              <a href={previewUrl} target="_blank" rel="noreferrer" onClick={() => setOpenedHere(true)} className="inline-flex items-center gap-1 text-stone-300 underline underline-offset-2 hover:text-white" data-testid="sow-preview-new-tab">
                Open as a PDF in a new tab <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>
        </div>
        <SpecialConditionsNotice conditions={state.special_conditions} />
        {onRequestChange && <ChangeRequestBox requests={state.change_requests} onRequest={onRequestChange} canWrite={!!state.can_write} />}
        {onBack && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-white/10 px-4 py-3 text-sm text-stone-300" data-testid="sow-switch-route">
            <span className="flex-1">Would you rather contract on your own paperwork (your MSA, services agreement or purchase order)? You can switch at any time before signing; the details you have entered are kept.</span>
            <button type="button" onClick={onBack} disabled={busy} className="self-start sm:self-auto shrink-0 inline-flex items-center gap-2 rounded-full border border-[#3EA8DC]/70 px-4 py-2 font-semibold text-[#9FD8F2] hover:bg-[#3EA8DC]/10 transition-colors disabled:opacity-50" data-testid="sow-switch-route-btn">
              Use our own contract instead
            </button>
          </div>
        )}
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5 pt-2 border-t border-white/10">
          <Stat tone="dark" label="Net total, excluding VAT" value={fmtMoney(totals.net_total, "£", 2)} testid="sow-net" />
          <Stat tone="dark" label="UK VAT at 20%" value={vatYes ? fmtMoney(vatAmount, "£", 2) : "Not applicable"} mono={vatYes} testid="sow-vat" />
          <Stat tone="dark" label="Total payable" value={<span className="font-semibold">{fmtMoney(total, "£", 2)}</span>} testid="sow-total" />
          <Stat tone="dark" label="Payment terms" value={totals.payment_terms} mono={false} />
        </dl>
      </Section>

      <Section n="06" title="Sign and commission" tone="signing" intro="Signing here has the same effect as signing on paper. The signed PDF is emailed to you and to Medicys the moment it is complete." testid="commission-signing">
        {!draftOpened && (
          <div className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="sow-signing-locked">
            <Lock className="w-4 h-4 shrink-0" />
            <span>Review the draft Statement of Work above to unlock this step.</span>
          </div>
        )}
        <div className={`flex flex-col gap-6 transition-opacity ${draftOpened ? "" : "opacity-40 pointer-events-none select-none"}`} aria-disabled={!draftOpened} data-testid="sow-signing-body">
        <Field label="Step 1 of 3: confirm your agreement" required group hint={terms ? "Your details above are now locked. Untick this box if you need to change them." : "The name and signature fields unlock once this box is ticked."}>
          <label className={`flex items-start gap-3.5 rounded-lg bg-white border-2 p-4 text-[15px] leading-relaxed text-stone-800 cursor-pointer transition-colors ${terms ? "border-[#1B4F8A]" : "border-amber-400"}`} data-testid="sow-terms-label" data-state={terms ? "checked" : "unchecked"}>
            <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} data-testid="sow-terms" className="mt-1 h-5 w-5 border-stone-400 data-[state=checked]:bg-[#1B4F8A] data-[state=checked]:border-[#1B4F8A]" />
            <span className="flex-1">
              I have read the Statement of Work and the Medicys Terms and Conditions of Business and I agree to them. I am authorised to commission this work on behalf of {company}.
            </span>
            <span className={`shrink-0 self-start rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide uppercase ${terms ? "bg-[#1B4F8A]/10 text-[#1B4F8A]" : "bg-amber-100 text-amber-800"}`} data-testid="sow-terms-pill">
              {terms ? "Confirmed" : "Tick to continue"}
            </span>
          </label>
        </Field>
        <div className={`grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-6 transition-opacity ${terms ? "" : "opacity-40 pointer-events-none select-none"}`} aria-disabled={!terms} data-testid="sow-signing-fields">
          <Field label="Step 2 of 3: your full name, as it should appear on the document" required>
            <TextInput value={nameToSign} onChange={setTypedName} placeholder="First name and surname" testid="sow-typed-name" autoComplete="name" />
            <p className="font-serif text-2xl text-stone-900 mt-3 min-h-[2rem]" aria-hidden>{nameToSign}</p>
          </Field>
          <Field label="Step 3 of 3: draw your signature" required group>
            <SignaturePad onChange={setSignaturePng} testid="sow-signature" />
          </Field>
        </div>
        {error && (
          <Notice tone="danger" testid="commission-error">
            {error}
            {invalid && invalid.size > 0 ? " We have unlocked your details above so you can complete them; tick the agreement box again when you are ready to sign." : ""}
          </Notice>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <PrimaryButton
            onClick={() => onSign({ signatory_name: nameToSign.trim(), signature_png: signaturePng, terms_accepted: terms })}
            disabled={busy || !ready}
            testid="sow-sign"
            size="lg"
          >
            {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing</>) : (<><PenLine className="w-4 h-4" /> Sign the Statement of Work</>)}
          </PrimaryButton>
          {!ready && <p className="text-sm text-stone-500" data-testid="sow-sign-hint">{!draftOpened ? "Review the draft Statement of Work first." : !terms ? "Tick the agreement box first." : !nameComplete ? "Type your first name and surname to continue." : "Draw your signature to continue."}</p>}
        </div>
        </div>
      </Section>
        </>
      )}
      <SowReviewModal open={reviewOpen} onClose={() => setReviewOpen(false)} previewUrl={previewUrl} company={company} onAgree={agreeInReview} />
    </div>
  );
}
